import { FC, useState } from 'react';
import { IoConstructOutline } from 'react-icons/io5';

import { Card, Flex, Stack, Text, ThemeIcon, VisuallyHidden } from '@mantine/core';

import { publicConfig } from '@public-config';

import { CompanyProfile } from 'models';

import { ProfileModal } from 'components/company-profile/components/ProfileModal';
import { ProfileRow, ProfileRowCarousel } from 'components/company-profile/components/ProfileRow';
import { HomepageTitle } from 'components/homepage/components/HomepageTitle';
import { IKImage } from 'components/ik-image/IKImage';
import { GridSection } from 'components/section/GridSection';
import { Section } from 'components/section/Section';

import { ServicesInfoHelpers } from '../helpers/ServicesInfoHelpers';

import cx from './Services.module.scss';

type Service = NonNullable<CompanyProfile['servicesInfo']>[number];

const services: Service[] = [
    {
        id: '1',
        tags: [],
        title: 'DCIDE – Collaborative Microgrid Design Platform',
        shortDescription:
            'Cloud-based software platform that enables engineers, developers, and energy consultants to collaboratively design, simulate, and validate direct current (DC) microgrids.',
        description:
            'DCIDE is a cloud-based software platform that enables engineers, developers, and energy consultants to collaboratively design, simulate, and validate direct current (DC) microgrids. It combines an intuitive visual editor with detailed electrical validation tools to accelerate early-stage design, ensure regulatory compliance, and reduce soft costs in distributed energy projects. Teams can model solar arrays, batteries, converters, and loads, simulate energy flows, and export fully validated designs ready for implementation.',
        capabilities: [
            {
                id: '1',
                title: 'Real-time Collaboration & Diagramming',
                description:
                    'Multiple users can co-create electrical diagrams simultaneously, manage revisions, and annotate designs directly in the browser without any local software installation.',
            },
            {
                id: '2',
                title: 'Power-Flow Simulation & Wire-Sizing Validation',
                description:
                    'Simulate voltage, current, and power flow under various load conditions while automatically verifying conductor sizing, voltage drop, and safety per IEC and NEC standards.',
            },
            {
                id: '3',
                title: 'Product-Catalog Integration',
                description:
                    'Integrate real-world components from partner manufacturers or custom product databases to auto-generate bills of materials and reduce manual sourcing time.',
            },
        ],
        keyDifferentiators: [
            'Cloud-based and team-friendly',
            'Drag-and-drop component design',
            'Built-in simulation and validation',
            'Used in NREL case study',
        ],
    },
    {
        id: '2',
        tags: [],
        title: 'DCIDE Controller',
        shortDescription:
            'Hardware-agnostic energy management and control platform built for low-voltage DC Microgrids.',
        description:
            'The DCIDE Controller is a hardware-agnostic energy management and control platform built for low-voltage DC Microgrids. It enables seamless integration of multiple power sources - such as PV, storage, and grid-tied converters - and manages them with smart algorithms for real-time optimization. Designed for flexibility and interoperability, the DCIDE Controller removes vendor lock-in, supports open protocols, and allows users to monitor, control, and expand systems remotely. Ideal for commercial buildings, off-grid applications, and industrial microgrids requiring scalable DC control.',
        capabilities: [
            {
                id: '1',
                title: 'Hardware-Agnostic Integration',
                description:
                    'Supports any inverter, converter, or storage system through open protocols like Modbus, CAN, and MQTT, ensuring smooth interoperability across vendors.',
            },
            {
                id: '2',
                title: 'Smart Energy Management',
                description:
                    'Implements advanced control strategies for peak shaving, energy arbitrage, and self-consumption to optimize grid interaction and reduce energy costs.',
            },
            {
                id: '3',
                title: 'Scalable & Remote Architecture',
                description:
                    'Supports modular installations, rapid commissioning, and remote diagnostics - enabling flexible scaling from pilot systems to full-scale microgrids.',
            },
        ],
        keyDifferentiators: [
            'Plug-and-play deployment',
            'Peak-shave & self-consumption modes',
            'Remote monitoring ready',
            'Avoids vendor lock-in',
        ],
    },
    {
        id: '3',
        tags: [],
        title: 'Turn-key Design & Deployment Services',
        shortDescription:
            'End-to-end engineering, procurement, and construction (EPC) services for low-voltage DC Microgrids and distributed energy systems.',
        description:
            'Direct Energy Partners provides end-to-end engineering, procurement, and construction (EPC) services for low-voltage DC Microgrids and distributed energy systems. From early feasibility studies to commissioning and performance optimization, DEP helps organizations transition from traditional AC infrastructure to efficient DC-based systems. Their multidisciplinary approach ensures reliable system integration, reduced operational costs, and long-term sustainability for both commercial and community-scale projects.',
        capabilities: [
            {
                id: '1',
                title: 'Feasibility & Modelling',
                description:
                    'Analyzes site data, load profiles, and generation potential to model energy flows, assess economic benefits, and define the most viable microgrid architecture.',
            },
            {
                id: '2',
                title: 'EPC & Integration',
                description:
                    'Delivers full design, procurement, and on-site integration support, ensuring that systems meet electrical, safety, and interoperability standards.',
            },
            {
                id: '3',
                title: 'Optimization & Advisory',
                description:
                    'Provides ongoing monitoring, diagnostics, and operational insights to enhance system performance and lower total cost of ownership.',
            },
        ],
        keyDifferentiators: [
            'Up to 30% lower TCO',
            'Higher efficiency vs AC grids',
            'Supports community projects',
            'Active in EU & US',
        ],
    },
];

const Services: FC<{ company: CompanyProfile }> = ({ company }) => {
    const [openedService, setOpenedService] = useState<Service | null>(null);

    const showServicesFallback =
        company?.slug === 'dep' && ['development', 'staging'].includes(publicConfig.environment);

    if (!showServicesFallback && !company.servicesInfo?.length) {
        return null;
    }

    const allServices = (company.servicesInfo?.length ? company.servicesInfo : services)
        .slice()
        .sort((a, b) => ServicesInfoHelpers.getCompletenessScore(b) - ServicesInfoHelpers.getCompletenessScore(a));

    return (
        <Section title="Services" id="services">
            <ProfileRowCarousel>
                {allServices.map((service) => (
                    <ProfileRow
                        key={service.id}
                        leading={<IoConstructOutline />}
                        title={service.title}
                        meta={service.shortDescription}
                        metaLines={3}
                        onClick={() => setOpenedService(service)}
                    />
                ))}
            </ProfileRowCarousel>

            <ProfileModal
                opened={!!openedService}
                onClose={() => setOpenedService(null)}
                size={1100}
                eyebrow="Service"
                eyebrowIcon={<IoConstructOutline />}
                title={openedService?.title}
            >
                {openedService && <ServiceCard {...openedService} />}
            </ProfileModal>

            <VisuallyHidden>
                {services.map((service) => (
                    <ServiceCard key={service.id} {...service} />
                ))}
            </VisuallyHidden>
        </Section>
    );
};

const ServiceCard = ({ title, description, capabilities, keyDifferentiators, image }: Service) => {
    return (
        <Section>
            <VisuallyHidden>{title}</VisuallyHidden>
            <Stack gap="xl">
                <Flex gap="xl" align="center" className={cx.details}>
                    <Text className={cx.detailsText}>{description}</Text>
                    {image && <IKImage fileOrId={image as any} width={400} className={cx.detailsImage} />}
                </Flex>

                {capabilities.length > 0 && (
                    <Stack className={cx.cardsSection}>
                        <HomepageTitle>What we offer</HomepageTitle>

                        <GridSection nbCols={3} className={cx.cardsGrid}>
                            {capabilities.map((capability) => (
                                <Card key={capability.id} withBorder className={cx.card}>
                                    <Text fz="lg" fw={800} mb="lg">
                                        {capability.title}
                                    </Text>

                                    <Text fz="lg">{capability.description}</Text>
                                </Card>
                            ))}
                        </GridSection>
                    </Stack>
                )}

                {keyDifferentiators.length > 0 && (
                    <Stack className={cx.cardsSection}>
                        <HomepageTitle>What we promise</HomepageTitle>

                        <GridSection nbCols={3} className={cx.cardsGrid}>
                            {keyDifferentiators.map((bullet, index) => (
                                <Card key={bullet} withBorder className={`${cx.card} ${cx.cardBullet}`}>
                                    <ThemeIcon
                                        size="lg"
                                        radius={99}
                                        fw={800}
                                        color="black"
                                        mb={8}
                                        className={cx.cardBulletIcon}
                                    >
                                        {index + 1}
                                    </ThemeIcon>

                                    <Text key={bullet} fz="lg" fw={800} ta="center">
                                        {bullet}
                                    </Text>
                                    {/* <Text fz="lg" mt="lg">TODO description {bullet}</Text> */}
                                </Card>
                            ))}
                        </GridSection>
                    </Stack>
                )}
            </Stack>
        </Section>
    );
};

export { Services };
